import React, { useState } from 'react';
import { Box, Container, Typography, Paper, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { Email, HourglassEmpty, ErrorOutline, Public, HeadsetMic } from '@mui/icons-material';

const policySections = [
  {
    id: "delivery-methods",
    number: "1",
    icon: <Email />,
    title: "Digital Delivery",
    content: "Since we deal primarily in software licenses (Windows OS, MS Office, Windows Server), all our products are delivered digitally via email. There are no physical shipments or shipping costs associated with these products."
  },
  {
    id: "processing-time",
    number: "2",
    icon: <HourglassEmpty />,
    title: "Processing Time",
    content: "Digital products are typically delivered instantly or within 10-15 minutes of successful payment confirmation. In rare cases where manual verification is required, delivery may take up to 24 hours. You will receive an email containing your product key and download/activation instructions."
  },
  {
    id: "incorrect-email",
    number: "3",
    icon: <ErrorOutline />,
    title: "Incorrect Email Address",
    content: "It is your responsibility to ensure the email address provided during checkout is accurate. If you haven't received your key within the expected timeframe, please check your Spam/Junk folder first. If it's still missing, contact our support team to verify the email address on file."
  },
  {
    id: "international",
    number: "4",
    icon: <Public />,
    title: "International Availability",
    content: "Our digital licenses are available for purchase globally, subject to regional activation restrictions if any (which will be clearly stated on the product page). Because delivery is via email, location does not affect shipping times."
  },
  {
    id: "support",
    number: "5",
    icon: <HeadsetMic />,
    title: "Delivery Support",
    content: "If you experience any issues receiving your product key or if the link provided in the email is not functioning, please reach out immediately to our dedicated technical support at arinfotechsolutions38@gmail.com."
  }
];

const ShippingPolicy = () => {
  const [activeSection, setActiveSection] = useState(policySections[0].id);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -115;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F1F5F9', pb: 10 }}>
      {/* Banner Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0F172A 0%, #0284C7 100%)', // Sky blue tint
          color: '#FFF',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center'
        }}
      >
        <motion.div
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            width: '100%',
            height: '2px',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
            pointerEvents: 'none'
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Shipping Policy
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
              Information regarding our digital delivery process.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: 3, md: 5 }, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4} lg={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 10px 30px -10px rgba(0,0,0,0.05)',
                position: { md: 'sticky' },
                top: { md: 100 },
                backgroundColor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B' }}>
                Table of Contents
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {policySections.map((section) => (
                  <Box
                    key={`toc-${section.id}`}
                    onClick={() => scrollToSection(section.id)}
                    sx={{
                      cursor: 'pointer',
                      py: 1,
                      px: 2,
                      borderRadius: 1,
                      transition: 'all 0.2s',
                      backgroundColor: activeSection === section.id ? 'rgba(2, 132, 199, 0.08)' : 'transparent',
                      color: activeSection === section.id ? '#0284C7' : '#64748B',
                      borderLeft: activeSection === section.id ? '3px solid #0284C7' : '3px solid transparent',
                      fontWeight: activeSection === section.id ? 600 : 400,
                      '&:hover': {
                        backgroundColor: 'rgba(2, 132, 199, 0.04)',
                        color: '#0284C7'
                      }
                    }}
                  >
                    <Typography variant="body2">{section.number}. {section.title}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {policySections.map((section, index) => (
                <motion.div
                  key={`content-${section.id}`}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                    <Paper
                      id={section.id}
                      elevation={2}
                      sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        scrollMarginTop: { xs: '105px', md: '120px' },
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                        border: '1px solid rgba(0,0,0,0.05)',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box sx={{
                        backgroundColor: '#0284C7',
                        color: '#FFF',
                        width: 44,
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '10%',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        flexShrink: 0
                      }}>
                        {section.number}
                      </Box>
                      <Box sx={{ color: '#0284C7', display: 'flex', opacity: 0.8 }}>
                        {section.icon}
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {section.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.8 }}>
                      {section.content}
                    </Typography>
                  </Paper>
                </motion.div>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ShippingPolicy;
